import { CuttingSheet } from './cutting-sheet.model';
import { FirestoreDataConverter, Timestamp } from '@angular/fire/firestore';
import { OrderState } from '../enums/order-state.enum';
import { TransporterEnum } from '../enums/transporter.enum';
import { PlanningCurrentHelper } from '../helpers/planning-current.helper';

export type InstallationAssignmentMode = 'automatic' | 'manual';
export type PlanningDelayType = 'breakdown' | 'reel_change';

export type PlanningOperationDelay = {
  id: string;
  type: PlanningDelayType;
  startAt: Date | null;
  durationMinutes: number;
  comment: string;
  createdBy: string;
  createdAt: Date | null;
}

/** States of the orders not validated yet (files, proof...): their work is only a forecast */
const FORECAST_ORDER_STATES: OrderState[] = [OrderState.NEW_ORDER, OrderState.WAIT_FILES, OrderState.PROCESSING,
  OrderState.WAIT_BAT, OrderState.BAT_ACCEPTED, OrderState.BAT_REFUSED];

/** Pose fixed at a date from the pose planning: its duration is spread over the opening hours of its poseurs */
export type PlanningInstallationPlan = {
  startAt: Date;
  durationHours: number;
  /** The planning organizes the production of the product to be finished before the pose */
  imperative: boolean;
  /** Pieces of a pose cut by hand, each with its own start and duration, adding up to the duration; none when whole */
  parts?: PlanningInstallationPart[];
  updatedBy: string;
  updatedAt: Date | null;
}

/** Piece of a pose cut by hand into several */
export type PlanningInstallationPart = {
  startAt: Date;
  durationHours: number;
}

/**
 * Why a pose must be moved: the production of its job does not meet a fixed one, or ends after one planned
 * automatically starts, or a poseur is booked twice
 */
export type PlanningInstallationAlert = {
  /** End of the production when it ends too late for the pose, null otherwise */
  productionEnd: Date | null;
  /** A production step is not planned yet */
  unplanned: boolean;
  /** The pose is planned automatically: it waits for no production step, which ends after it starts */
  automatic: boolean;
  /** Other poses booking one of its poseurs at the same time */
  overlaps: { operationId: string; jobNumber: number | null }[];
}

/**
 * Date read from Firestore or already built
 * @param value Timestamp, Date or nothing
 * @returns Date, or null
 */
function toDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return value;
  }
  return (value as Timestamp)?.toDate?.() ?? null;
}

/**
 * Normalize the pose plan read from Firestore
 * @param value Raw plan
 * @returns Plan, or null when none is set
 */
function parseInstallationPlan(value: unknown): PlanningInstallationPlan | null {
  const plan = value as Partial<Record<keyof PlanningInstallationPlan, unknown>> | null;
  const startAt = toDate(plan?.startAt);
  const durationHours = Number(plan?.durationHours);
  if (!startAt || !(durationHours > 0)) {
    return null;
  }
  const parts = (Array.isArray(plan?.parts) ? plan.parts as Partial<Record<keyof PlanningInstallationPart, unknown>>[] : [])
    .map(part => ({ startAt: toDate(part?.startAt), durationHours: Number(part?.durationHours) }))
    .filter((part): part is PlanningInstallationPart => Boolean(part.startAt) && part.durationHours > 0);
  return {
    startAt,
    durationHours,
    imperative: plan?.imperative === true,
    ...(parts.length > 1 ? { parts } : {}),
    updatedBy: String(plan?.updatedBy ?? ''),
    updatedAt: toDate(plan?.updatedAt),
  };
}

/**
 * Normalize the pose alert read from Firestore
 * @param value Raw alert
 * @returns Alert, or null when the production is on time
 */
function parseInstallationAlert(value: unknown): PlanningInstallationAlert | null {
  const alert = value as Partial<Record<keyof PlanningInstallationAlert, unknown>> | null;
  if (!alert) {
    return null;
  }
  const overlaps = Array.isArray(alert.overlaps) ? alert.overlaps : [];
  return {
    productionEnd: toDate(alert.productionEnd),
    unplanned: alert.unplanned === true,
    automatic: alert.automatic === true,
    overlaps: overlaps.map((overlap: { operationId?: unknown; jobNumber?: unknown }) => ({
      operationId: String(overlap?.operationId ?? ''),
      jobNumber: overlap?.jobNumber == null ? null : Number(overlap.jobNumber),
    })),
  };
}

/**
 * Normalize delay payloads read from Firestore
 * @param value Raw delays
 * @returns Delays with dates
 */
function parsePlanningDelays(value: unknown): PlanningOperationDelay[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((delay: Partial<PlanningOperationDelay> & {
    startAt?: Timestamp | Date;
    createdAt?: Timestamp | Date;
  }): PlanningOperationDelay => ({
    id: String(delay?.id ?? ''),
    type: delay?.type === 'reel_change' ? 'reel_change' : 'breakdown',
    startAt: delay?.startAt instanceof Date ? delay.startAt :
      (delay?.startAt as Timestamp)?.toDate?.() ?? null,
    durationMinutes: Number(delay?.durationMinutes) || 0,
    comment: String(delay?.comment ?? ''),
    createdBy: String(delay?.createdBy ?? ''),
    createdAt: delay?.createdAt instanceof Date ? delay.createdAt :
      (delay?.createdAt as Timestamp)?.toDate?.() ?? null,
  })).filter(delay => delay.durationMinutes > 0);
}

/**
 * Operation planned automatically from the Multipress pushJob synchronization
 */
export class PlanningOperation {
  id: string;
  organizationId: string;
  orderId: string;
  customerId: string;
  jobNumber: number;
  deliveryDate: Date | null;
  operation: string;
  department: number;
  calculatedTime: number;
  ordering: number;
  material: string;
  machine: string;
  machineId: number;
  sourceMachine: string;
  sourceMachineId: number;
  machineOrder: number;
  information: string;
  /** A pose entered by hand: no order nor job behind it, its title is the product name and its comment the information */
  /** Installation operation: lives in the pose agenda, apart from the production planning */
  isPose: boolean;
  manualPose: boolean;
  printingColors: string;
  printQuality: string;
  /** Paper type of the product sub-job technical data, shown next to the Multipress material */
  paperType: string;
  /** Cutting scheme of the product sub-job, read from Multipress at sync */
  cutting: CuttingSheet[];
  extraInfo: string;
  splited: string;
  machineQueueOrder: number | null;
  projectedStart: Date | null;
  projectedEnd: Date | null;
  queueSlotStart: Date | null;
  actualStart: Date | null;
  actualEnd: Date | null;
  actualStartedBy: string;
  actualEndedBy: string;
  productionDeadline: Date | null;
  /** Business days kept for the transport (0 for a customer pickup), null when not computed */
  transportLeadDays: number | null;
  /** The product of a customer with its own carrier leaves with it, not as a pickup: the agenda names the customer */
  customerCarrier: boolean;
  /** Transporter of a delivery that no carrier makes, such as a courier: the agenda names it by the departure */
  transporter: TransporterEnum | null;
  deadlineExceeded: boolean;
  accountPriority: number;
  orderState: OrderState;
  productPoId: string;
  productName: string;
  productFilePath: string;
  quantity: number;
  installationResourceIds: string[];
  installationAssignmentMode: InstallationAssignmentMode;
  delays: PlanningOperationDelay[];
  /** Pose fixed at a date from the pose planning */
  installationPlan: PlanningInstallationPlan | null;
  /** Computed by the planning: the production does not meet the fixed pose */
  installationAlert: PlanningInstallationAlert | null;
  canPunch: boolean;
  /** Frozen by the recalculation (block starting within the coming hour): kept where it is until unfrozen */
  frozen: boolean;
  /** Heads the punch queue of its machine, even where punching in any order makes every item punchable */
  isQueueHead: boolean;
  archived: boolean;
  updatedAt: Date | null;

  constructor(operation?: Partial<PlanningOperation>) {
    this.id = operation?.id ?? '';
    this.organizationId = operation?.organizationId ?? '';
    this.orderId = operation?.orderId ?? '';
    this.customerId = operation?.customerId ?? '';
    this.jobNumber = operation?.jobNumber ?? 0;
    this.deliveryDate = operation?.deliveryDate ?? null;
    this.operation = operation?.operation ?? '';
    this.department = operation?.department ?? 0;
    this.calculatedTime = operation?.calculatedTime ?? 0;
    this.ordering = operation?.ordering ?? 0;
    this.material = operation?.material ?? '';
    this.machine = operation?.machine ?? '';
    this.machineId = operation?.machineId ?? 0;
    this.sourceMachine = operation?.sourceMachine ?? this.machine;
    this.sourceMachineId = operation?.sourceMachineId ?? this.machineId;
    this.machineOrder = operation?.machineOrder ?? 0;
    this.information = operation?.information ?? '';
    this.isPose = operation?.isPose ?? false;
    this.manualPose = operation?.manualPose ?? false;
    this.printingColors = operation?.printingColors ?? '';
    this.printQuality = operation?.printQuality ?? '';
    this.paperType = operation?.paperType ?? '';
    this.cutting = operation?.cutting ?? [];
    this.extraInfo = operation?.extraInfo ?? '';
    this.splited = operation?.splited ?? '';
    this.machineQueueOrder = operation?.machineQueueOrder ?? null;
    this.projectedStart = operation?.projectedStart ?? null;
    this.projectedEnd = operation?.projectedEnd ?? null;
    this.queueSlotStart = operation?.queueSlotStart ?? null;
    this.actualStart = operation?.actualStart ?? null;
    this.actualEnd = operation?.actualEnd ?? null;
    this.actualStartedBy = operation?.actualStartedBy ?? '';
    this.actualEndedBy = operation?.actualEndedBy ?? '';
    this.productionDeadline = operation?.productionDeadline ?? null;
    this.transportLeadDays = operation?.transportLeadDays ?? null;
    this.customerCarrier = operation?.customerCarrier ?? false;
    this.transporter = operation?.transporter ?? null;
    this.deadlineExceeded = operation?.deadlineExceeded ?? false;
    this.accountPriority = Number(operation?.accountPriority) || 0;
    this.orderState = operation?.orderState ?? OrderState.PRODUCTION;
    this.archived = operation?.archived ?? false;
    this.productPoId = operation?.productPoId ?? '';
    this.productName = operation?.productName ?? '';
    this.productFilePath = operation?.productFilePath ?? '';
    this.quantity = operation?.quantity ?? 0;
    this.installationResourceIds = [...(operation?.installationResourceIds ?? [])];
    this.installationAssignmentMode = operation?.installationAssignmentMode ?? 'automatic';
    this.delays = parsePlanningDelays(operation?.delays);
    this.installationPlan = parseInstallationPlan(operation?.installationPlan);
    this.installationAlert = parseInstallationAlert(operation?.installationAlert);
    this.canPunch = Boolean(operation?.canPunch);
    this.frozen = Boolean(operation?.frozen);
    this.isQueueHead = Boolean(operation?.isQueueHead ?? operation?.canPunch);
    this.updatedAt = operation?.updatedAt ?? null;
  }

  /**
   * Hours of work planned: the duration set on the pose plan, otherwise the Multipress time
   * @returns Hours
   */
  get plannedHours(): number {
    return this.installationPlan?.durationHours ?? this.calculatedTime;
  }

  /**
   * Planned duration in hours: planned time plus declared delays
   * @returns Duration used to project an unpunched operation
   */
  get durationHours(): number {
    return this.plannedHours + this.delayDurationMs / 3600000;
  }

  /**
   * Whether the pose stays at the date set from the pose planning: fixed and not started yet
   * @returns True when fixed
   */
  get isFixedInstallation(): boolean {
    return Boolean(this.installationPlan) && !this.isStarted && !this.isFinished;
  }

  /**
   * Total declared delay duration
   * @returns Duration in minutes
   */
  get delayDurationMinutes(): number {
    return this.delays.reduce((total, delay) => {
      if (!Number.isInteger(delay.durationMinutes) || delay.durationMinutes <= 0) {
        return total;
      }
      return total + delay.durationMinutes;
    }, 0);
  }

  /**
   * Total declared delay duration
   * @returns Duration in milliseconds
   */
  get delayDurationMs(): number {
    return this.delayDurationMinutes * 60000;
  }

  /**
   * Whether the operation has a declared real start
   * @returns True when started
   */
  get isStarted(): boolean {
    return Boolean(this.actualStart);
  }

  /**
   * Whether the operation has a declared real end
   * @returns True when finished
   */
  get isFinished(): boolean {
    return Boolean(this.actualEnd);
  }

  /**
   * Customer account type encoded by the backend planning priority
   * @returns key_account, vip, or empty for a standard account
   */
  get accountPriorityType(): 'key_account' | 'vip' | '' {
    if (this.accountPriority >= 2) {
      return 'key_account';
    }
    if (this.accountPriority >= 1) {
      return 'vip';
    }
    return '';
  }

  /**
   * Whether the job should show a priority badge in the agenda
   * @returns True for key accounts and VIP
   */
  get isPriorityAccount(): boolean {
    return Boolean(this.accountPriorityType);
  }

  /**
   * Whether the planned slot contains the given instant
   * @param at Instant to test
   * @returns True when the unfinished operation is theoretically running
   */
  isTheoreticallyInProgress(at: Date): boolean {
    return PlanningCurrentHelper.isCurrent(this, at);
  }

  /**
   * Whether a delay can be declared on this unfinished current slot
   * @param at Instant to test
   * @returns True when the operation is theoretically in progress
   */
  canDeclareDelay(at: Date): boolean {
    return PlanningCurrentHelper.canDeclareDelay(this, at);
  }

  /**
   * Whether the operation is part of the forecast: still to do, of an order not validated yet. It cannot be punched.
   * @returns True for a forecast operation
   */
  get isForecast(): boolean {
    return FORECAST_ORDER_STATES.includes(this.orderState) && !this.isStarted && !this.isFinished;
  }

  /**
   * Whether the pose is cut by hand into several pieces placed on their own dates
   * @returns True when it has several placed pieces and is not started
   */
  get isCut(): boolean {
    return (this.installationPlan?.parts?.length ?? 0) > 1 && !this.isStarted && !this.isFinished;
  }

  /**
   * Start shown on the tile: real punch if started, otherwise the projected slot
   * @returns Display start
   */
  get displayStart(): Date | null {
    if (this.actualStart) {
      return this.actualStart;
    }
    if (this.actualEnd) {
      const durationMs = Math.max(this.plannedHours, 1 / 60) * 3600000;
      return new Date(this.actualEnd.getTime() - durationMs);
    }
    return this.projectedStart ?? this.queueSlotStart;
  }

  /**
   * End shown on the tile: real punch if finished, otherwise projected (Multipress + delays)
   * @returns Display end
   */
  get displayEnd(): Date | null {
    return this.actualEnd ?? this.projectedEnd;
  }

  /**
   * Whether the operation can be drawn on the planning timeline
   * @returns True when a start time is available
   */
  get hasDisplayInterval(): boolean {
    return Boolean(this.displayStart);
  }

  /**
   * Expected end time shown in the planning list
   * @returns Projected end, or actual end when the task is punched
   */
  get scheduleEnd(): Date | null {
    if (this.isFinished) {
      return this.actualEnd;
    }
    return this.projectedEnd;
  }

  /**
   * Product name and quantity shown on the calendar and lists
   * @returns Product label, or an empty string when unmatched
   */
  get productLabel(): string {
    const name = this.productName.trim();
    if (!name) {
      return '';
    }
    return this.quantity > 0 ? `${name} × ${this.quantity}` : name;
  }

  /**
   * Stable identity used for punch merge and anomalies
   * @returns Identity key
   */
  get identityKey(): string {
    return PlanningOperation.identityKey(this);
  }

  /**
   * Stable identity used for punch merge and anomalies
   * @param operation Operation-like object
   * @returns Identity key
   */
  static identityKey(operation?: {
    jobNumber?: number;
    productPoId?: string;
    sourceMachineId?: number;
    machineId?: number;
    ordering?: number;
    machineOrder?: number;
    operation?: string;
  } | null): string {
    const productPoId = String(operation?.productPoId ?? '').trim().replace(/[|/]/g, '_');
    const name = String(operation?.operation ?? '').trim().replace(/[|/]/g, '_');
    const sourceMachineId = Number(operation?.sourceMachineId) || Number(operation?.machineId) || 0;
    return [
      Number(operation?.jobNumber) || 0,
      productPoId,
      sourceMachineId,
      Number(operation?.ordering) || 0,
      Number(operation?.machineOrder) || 0,
      name,
    ].join('|');
  }

  /**
   * Converter used to convert object from firebase
   */
  static converter: FirestoreDataConverter<PlanningOperation> = {
    toFirestore: (operation: PlanningOperation) => ({
      organizationId: operation.organizationId,
      orderId: operation.orderId,
      customerId: operation.customerId,
      jobNumber: operation.jobNumber,
      deliveryDate: operation.deliveryDate,
      operation: operation.operation,
      department: operation.department,
      calculatedTime: operation.calculatedTime,
      ordering: operation.ordering,
      material: operation.material,
      machine: operation.machine,
      machineId: operation.machineId,
      sourceMachine: operation.sourceMachine,
      sourceMachineId: operation.sourceMachineId,
      machineOrder: operation.machineOrder,
      information: operation.information,
      isPose: operation.isPose,
      manualPose: operation.manualPose,
      printingColors: operation.printingColors,
      printQuality: operation.printQuality,
      paperType: operation.paperType,
      cutting: operation.cutting,
      extraInfo: operation.extraInfo,
      splited: operation.splited,
      machineQueueOrder: operation.machineQueueOrder,
      actualStart: operation.actualStart,
      actualEnd: operation.actualEnd,
      actualStartedBy: operation.actualStartedBy,
      actualEndedBy: operation.actualEndedBy,
      productionDeadline: operation.productionDeadline,
      transportLeadDays: operation.transportLeadDays,
      customerCarrier: operation.customerCarrier,
      transporter: operation.transporter,
      deadlineExceeded: operation.deadlineExceeded,
      accountPriority: operation.accountPriority,
      orderState: operation.orderState,
      productPoId: operation.productPoId,
      productName: operation.productName,
      productFilePath: operation.productFilePath,
      quantity: operation.quantity,
      installationResourceIds: operation.installationResourceIds,
      installationAssignmentMode: operation.installationAssignmentMode,
      delays: operation.delays,
      installationPlan: operation.installationPlan,
      updatedAt: operation.updatedAt,
    }),
    fromFirestore: (snapshot, options) => {
      const data = snapshot.data(options);
      return new PlanningOperation({
        ...data,
        id: snapshot.id,
        deliveryDate: (data['deliveryDate'] as Timestamp)?.toDate() ?? null,
        projectedStart: (data['projectedStart'] as Timestamp)?.toDate() ?? null,
        projectedEnd: (data['projectedEnd'] as Timestamp)?.toDate() ?? null,
        queueSlotStart: (data['queueSlotStart'] as Timestamp)?.toDate() ?? null,
        actualStart: (data['actualStart'] as Timestamp)?.toDate() ?? null,
        actualEnd: (data['actualEnd'] as Timestamp)?.toDate() ?? null,
        productionDeadline: (data['productionDeadline'] as Timestamp)?.toDate() ?? null,
        updatedAt: (data['updatedAt'] as Timestamp)?.toDate() ?? null,
        delays: parsePlanningDelays(data['delays']),
      });
    }
  };
}
